import { PANEL_PRODUCT_CELL_TYPES_CODE } from '@/constants/panelProduct';
import { englishDefectTypeName } from '@/utils/defect/defectTypeDisplay';

const KO_DATE_RE = /(\d{4})년\s*(\d{1,2})월\s*(\d{1,2})일(?:\s*,\s*(\d{1,2})시)?/;

function pad2(value) {
  return String(value).padStart(2, '0');
}

function isBlank(value) {
  return value === null || value === undefined || String(value).trim() === '';
}

/**
 * 보고서 API가 내려주는 한국어 날짜/일시 문자열을 분해한다.
 * @returns {{ year: number, month: number, day: number, hour: number|null, hasTime: boolean } | null}
 */
export function parseReportDateParts(value) {
  if (isBlank(value)) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return {
      year: value.getFullYear(),
      month: value.getMonth() + 1,
      day: value.getDate(),
      hour: value.getHours(),
      hasTime: true
    };
  }

  const str = String(value).trim();
  const ko = str.match(KO_DATE_RE);
  if (ko) {
    const hour = ko[4] != null ? Number(ko[4]) : null;
    return {
      year: Number(ko[1]),
      month: Number(ko[2]),
      day: Number(ko[3]),
      hour,
      hasTime: hour != null
    };
  }

  const parsed = new Date(str);
  if (Number.isNaN(parsed.getTime())) return null;
  const hasTime = /시|T\d{2}|:\d{2}/.test(str);
  return {
    year: parsed.getFullYear(),
    month: parsed.getMonth() + 1,
    day: parsed.getDate(),
    hour: hasTime ? parsed.getHours() : null,
    hasTime
  };
}

/**
 * 보고서 날짜를 현재 UI 언어에 맞게 표시한다.
 * @param {string|Date|null|undefined} value
 * @param {string} locale 'ko' | 'en'
 * @param {{ includeTime?: boolean }} [options]
 */
export function formatReportDate(value, locale = 'ko', options = {}) {
  if (isBlank(value)) return '-';
  const parts = parseReportDateParts(value);
  if (!parts) return String(value);

  const includeTime = options.includeTime ?? parts.hasTime;
  const isEn = String(locale || '').toLowerCase().startsWith('en');
  const date = new Date(parts.year, parts.month - 1, parts.day, parts.hour ?? 0);

  if (isEn) {
    const datePart = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    if (!includeTime || parts.hour == null) return datePart;
    return `${datePart}, ${pad2(parts.hour)}:00`;
  }

  const datePart = `${parts.year}년 ${parts.month}월 ${parts.day}일`;
  if (!includeTime || parts.hour == null) return datePart;
  return `${datePart}, ${parts.hour}시`;
}

export function defectTypeNameKey(code) {
  const normalized = String(code || '').trim().toUpperCase();
  return normalized ? `defectTypeNames.${normalized}` : '';
}

export function defectTypeDescriptionKey(code) {
  const normalized = String(code || '').trim().toUpperCase();
  return normalized ? `defectTypes.${normalized}` : '';
}

export function localizeDefectTypeLabel(te, t, code, fallback) {
  void te;
  void t;
  const name = englishDefectTypeName(code, fallback);
  if (isBlank(name)) return '-';
  return name;
}

export function localizeDefectTypeDescription(te, t, code, fallback) {
  const key = defectTypeDescriptionKey(code);
  if (key && typeof te === 'function' && te(key)) {
    return t(key);
  }
  if (isBlank(fallback)) return '-';
  return fallback;
}

/**
 * 패널 셀 타입 enum 이름 또는 숫자 코드를 cellTypeDetail 숫자 코드로 정규화한다.
 * @returns {number|null}
 */
export function normalizePanelProductCellTypeCode(raw) {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  const str = String(raw).trim();
  if (/^\d+$/.test(str)) return Number(str);
  const fromEnum = PANEL_PRODUCT_CELL_TYPES_CODE[str.toUpperCase()];
  return fromEnum || null;
}
